// Shared constants for the loading screen that's rendered in two places:
//   1. packages/sdk/src/client.ts — host-page overlay (vanilla DOM, before
//      the passkey iframe paints).
//   2. apps/passkey/src/components/dialog/screens/Loading.tsx — same screen
//      drawn by the iframe via React + DialogShell + IconWithStatus.
//
// The handoff between the two is a crossfade on `PASSKEY_RENDERED`, so any
// drift in geometry / colour / copy produces a visible flash. SDK-side
// values live here instead of being inlined; the iframe side imports from
// here too. If a value isn't in this file, BOTH sides duplicated it inline
// — drift is now likely.
//
// Theme tokens are mirrored from apps/passkey/src/app/globals.css. If the
// CSS values change there, update the LIGHT/DARK pairs below in the same PR.

// === Spinner geometry =====================================================
// 82×82 SVG with two concentric circles; the foreground arc covers 17 % of
// the circumference and rotates via CSS `animate-spin`.
export const SPINNER_SIZE = 82;
export const SPINNER_STROKE = 2.4118;
export const SPINNER_RADIUS = (SPINNER_SIZE - SPINNER_STROKE) / 2;
export const SPINNER_CIRCUMFERENCE = 2 * Math.PI * SPINNER_RADIUS;
export const SPINNER_ARC_FRACTION = 0.17;
export const SPINNER_ARC = SPINNER_CIRCUMFERENCE * SPINNER_ARC_FRACTION;
// One full rotation, in ms. Shared so the SDK host-page overlay and the
// iframe's `IconWithStatus` spinner rotate at the SAME rate — otherwise the
// arc visibly changes speed at the PASSKEY_RENDERED crossfade and the two
// loading screens read as different. The iframe side previously inherited
// Tailwind's `animate-spin` default (1s) while the SDK hardcoded 0.8s.
export const SPINNER_SPIN_DURATION_MS = 800;

// === Modal geometry =======================================================
// Mirrors apps/passkey DialogShell's full-viewport iframe placement:
// centered desktop card at Tailwind's `sm` breakpoint, bottom sheet below it.
// Source of truth: Figma "Sizing changes" frame (3036:9897) — Modal width
// is 380px on desktop. Must stay in sync with `--spacing-modal-width` /
// `--spacing-modal-padding` in apps/passkey/src/app/globals.css and with
// the SDK's POPUP_WIDTH / DEFAULT_EMBED_WIDTH in packages/sdk/src/client.ts.
export const LOADING_MODAL_WIDTH = 380;
export const LOADING_MODAL_PADDING = 12;
export const LOADING_MODAL_RADIUS = 16;
export const LOADING_MOBILE_BREAKPOINT = 640;

// === Spacing (inside the card) ============================================
// Vertical rhythm between body and footer, and within the body block.
// 16px between body and footer is the same value DialogShell uses for the
// outer column gap; we keep them in lockstep so embedded dialogs share the
// rhythm too.
export const LOADING_CARD_GAP = 16;
export const LOADING_BODY_GAP = 12;
export const LOADING_ICON_PADDING = 12;
export const LOADING_TITLE_BLOCK_GAP = 4;
export const LOADING_FOOTER_GAP = 4;

// === Typography ===========================================================
// Title + subtitle + footer. `line-height: normal` is critical: the host
// page may set a different value on <body> (Tailwind preflight 1.5, etc)
// and inheritance would otherwise blow up the card height.
export const LOADING_TITLE_FONT_SIZE = 20;
export const LOADING_TITLE_FONT_WEIGHT = 700;
export const LOADING_SUBTITLE_FONT_SIZE = 12;
export const LOADING_SUBTITLE_FONT_WEIGHT = 500;
export const LOADING_FOOTER_FONT_SIZE = 11;
export const LOADING_FOOTER_FONT_WEIGHT = 500;
export const LOADING_LINE_HEIGHT = "normal" as const;
// Font stack matches `data-connect-v2` in apps/passkey globals.css. Inter
// is loaded by the host app; the rest are safe fallbacks. Includes emoji
// stack for screens that render copy with emoji characters.
export const LOADING_FONT_FAMILY =
  "'Inter', ui-sans-serif, system-ui, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji'";

// === Rhinestone wordmark ==================================================
// Footer "Powered by [logo]". Source SVG is 72×16; both consumers render
// at native size.
export const LOGO_WIDTH = 72;
export const LOGO_HEIGHT = 16;

// === Brand colour =========================================================
// Mirrors `--color-primary-500` in apps/passkey globals.css.
export const BRAND_PURPLE = "#685bff";

// === Theme tokens =========================================================
// Hex pairs mirroring the iframe's `light-dark()` CSS custom properties.
// The host-page SDK overlay can't read those custom properties (different
// document), so it picks light/dark from the `theme` URL param and falls
// back to `prefers-color-scheme`.
export type LoadingThemeTokens = {
  /** `--color-modal-bg` */
  bgPrimary: string;
  /** `--color-modal-border` */
  borderColor: string;
  /** `--color-view-text-primary` */
  textPrimary: string;
  /** `--color-view-text-secondary` */
  textSecondary: string;
};

export const LIGHT_TOKENS: LoadingThemeTokens = {
  bgPrimary: "#ffffff",
  borderColor: "#f4f4f5",
  textPrimary: "#27272a",
  textSecondary: "#71717b",
};

export const DARK_TOKENS: LoadingThemeTokens = {
  bgPrimary: "#0a0a0a",
  borderColor: "#18181b",
  textPrimary: "#e4e4e7",
  textSecondary: "#9f9fa9",
};

// `--color-modal-text-footer` — a single value across both themes.
export const FOOTER_TEXT_COLOR = "#71717b";

// === Copy =================================================================
export const LOADING_TITLE = "Loading...";
export const LOADING_SUBTITLE =
  "This will only take a few moments, do not close the window.";
